/**
 * useAuthGuard — Defense-in-depth runtime auth check.
 *
 * Usage inside any dashboard layout:
 *   useAuthGuard(['student']);              // role-specific
 *   useAuthGuard(['super_admin', 'tutor']); // multiple roles
 *
 * Behaviour:
 *  1. If no token in localStorage → redirect to /auth immediately.
 *  2. Verifies token via GET /api/users/me.
 *  3. If API returns 401/403 or any error → clears token + redirect to /auth.
 *  4. If logged-in user's role is not in allowedRoles → redirect to /auth.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const useAuthGuard = (allowedRoles = []) => {
    const navigate = useNavigate();

    useEffect(() => {
        const verify = async () => {
            const token = localStorage.getItem('token');

            // 1. No token at all — kick immediately
            if (!token) {
                navigate('/auth', { replace: true });
                return;
            }

            try {
                // 2. Verify token against the server
                const res = await axios.get('/api/users/me', {
                    headers: { Authorization: `Bearer ${token}` }
                });

                const role = res.data?.role;

                // 3. Role check (if specific roles required)
                if (allowedRoles.length > 0 && !allowedRoles.includes(role)) {
                    localStorage.removeItem('token');
                    navigate('/auth', { replace: true });
                }
            } catch (err) {
                // 4. 401 / 403 / network — token invalid or expired
                localStorage.removeItem('token');
                navigate('/auth', { replace: true });
            }
        };

        verify();
        // Re-run whenever the route changes (layout re-mounts)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
};

export default useAuthGuard;
